/**
 * CoachInput — the exact shape of the JSONB packet returned by the Postgres
 * function `public.coach_gather_window(p_user uuid, p_days integer)`
 * (see `infrastructure/supabase/migrations/20260101000001_init.sql` §14).
 *
 * Every field name, nullability and casing here mirrors the SQL `jsonb_build_object`
 * keys one-for-one. Where the SQL can emit `null` (nullable user columns such as
 * `birthYear`, `bodyweightKg`, `premiumUntil`, `targetRpe`, `targetWeightKg`,
 * `avgRpe`, `lastSessionWeek`) the zod schema accepts `null`; where an array can
 * be empty the schema accepts `[]`. Never relax this file by hand — regenerate the
 * packet with `select coach_gather_window('<uuid>', 28)` and check it against
 * `test/fixtures/coach-input.json`.
 */
import { z } from 'zod';

const uuidSchema = z.string().uuid();

/** numeric columns arrive as JSON numbers; nullable numeric columns as `number | null`. */
const nullableNumber = z.number().nullable();
const nullableInt = z.number().int().nullable();

export const UnitSystemSchema = z.enum(['metric', 'imperial']);
export const AccountTierSchema = z.enum(['basic', 'premium']);
export const ExperienceSchema = z.enum(['beginner', 'intermediate', 'advanced']);
export const RepBucketSchema = z.enum(['1-3', '4-6', '7-10', '11+']);
export const ProgressionRuleSchema = z.enum([
  'double_progression',
  'linear',
  'percentage',
  'rpe_autoreg',
  'coach_discretion',
]);

/**
 * `users` row, mirrored as `profile`. The packet always contains `id` because the
 * SQL coalesces to `'{}'::jsonb` for an unknown user, in which case every key is
 * absent — so `id` is required but the rest are optional.
 */
export const CoachProfileSchema = z.object({
  id: uuidSchema,
  displayName: z.string().nullable().optional(),
  unitSystem: UnitSystemSchema.optional(),
  tier: AccountTierSchema.optional(),
  timezone: z.string().optional(),
  bodyweightKg: nullableNumber.optional(),
  heightCm: nullableNumber.optional(),
  birthYear: nullableInt.optional(),
  trainingGoal: z.string().nullable().optional(),
  experience: ExperienceSchema.nullable().optional(),
  injuryFlags: z.array(z.string()).optional(),
  weeklyTarget: z.number().int().optional(),
  premiumUntil: z.string().nullable().optional(),
});
export type CoachProfile = z.infer<typeof CoachProfileSchema>;

/** `streaks` row. An untouched user yields `'{}'` so every key is optional. */
export const CoachStreakSchema = z.object({
  currentWeeks: z.number().int().optional(),
  longestWeeks: z.number().int().optional(),
  lastSessionWeek: z.string().nullable().optional(),
  freezesRemaining: z.number().int().optional(),
});
export type CoachStreak = z.infer<typeof CoachStreakSchema>;

/** One element of `sessions[]` — a row of `v_session_totals` (completed only). */
export const CoachSessionSchema = z.object({
  sessionId: uuidSchema,
  label: z.string().nullable(),
  startedAt: z.string(),
  volumeKg: z.number(),
  setsCompleted: z.number().int(),
  prCount: z.number().int(),
  failedSets: z.number().int(),
  avgRpe: nullableNumber,
  durationMin: nullableInt,
});
export type CoachSession = z.infer<typeof CoachSessionSchema>;

/**
 * One optional per-set row inside `exerciseTrend[]`.
 *
 * The production `coach_gather_window` aggregates at week level, so this key is
 * normally absent; it exists so callers can attach exact set data (from
 * `logged_sets`) without changing the packet contract. When present it makes the
 * double-progression decision exact instead of inferred. See `progression.ts`.
 */
export const CoachSetSchema = z.object({
  setIndex: z.number().int(),
  weightKg: z.number(),
  reps: z.number().int(),
  rpe: nullableNumber.optional(),
  status: z.string(),
  isPr: z.boolean().optional(),
});
export type CoachSet = z.infer<typeof CoachSetSchema>;

/** One element of `exerciseTrend[]` — a row of `v_weekly_exercise_progress`. */
export const ExerciseTrendPointSchema = z.object({
  exerciseId: uuidSchema,
  exerciseName: z.string(),
  /** ISO date (Monday) of the training week, e.g. `2026-09-14`. */
  weekStart: z.string(),
  workingSets: z.number().int(),
  volumeKg: z.number(),
  topWeightKg: nullableNumber,
  bestE1rmKg: nullableNumber,
  avgRpe: nullableNumber,
  failedSets: z.number().int(),
  sets: z.array(CoachSetSchema).optional(),
});
export type ExerciseTrendPoint = z.infer<typeof ExerciseTrendPointSchema>;

/** One element of `personalRecords[]`. */
export const PersonalRecordSchema = z.object({
  exerciseId: uuidSchema,
  exerciseName: z.string(),
  repBucket: RepBucketSchema,
  weightKg: z.number(),
  reps: z.number().int(),
  estimated1rmKg: z.number(),
  achievedAt: z.string(),
});
export type PersonalRecord = z.infer<typeof PersonalRecordSchema>;

/** `plan_exercises` joined to `exercises` — the prescription the coach may rewrite. */
export const PlanExerciseSchema = z.object({
  planExerciseId: uuidSchema,
  exerciseId: uuidSchema,
  name: z.string(),
  orderIndex: z.number().int(),
  sets: z.number().int(),
  repMin: z.number().int(),
  repMax: z.number().int(),
  targetRpe: nullableNumber,
  targetWeightKg: nullableNumber,
  restSeconds: z.number().int(),
  progressionRule: ProgressionRuleSchema,
});
export type PlanExercise = z.infer<typeof PlanExerciseSchema>;

export const PlanDaySchema = z.object({
  dayId: uuidSchema,
  label: z.string(),
  dayIndex: z.number().int(),
  exercises: z.array(PlanExerciseSchema),
});
export type PlanDay = z.infer<typeof PlanDaySchema>;

/** `workout_plans` + `plan_days` + `plan_exercises`, or `null` when no active plan. */
export const ActivePlanSchema = z.object({
  planId: uuidSchema,
  name: z.string(),
  week: z.number().int(),
  days: z.array(PlanDaySchema),
});
export type ActivePlan = z.infer<typeof ActivePlanSchema>;

/** `v_plan_adherence` aggregate over the window. */
export const ComplianceSchema = z.object({
  avgCompliancePct: nullableNumber.optional(),
  sessionsInWindow: z.number().int().optional(),
});
export type Compliance = z.infer<typeof ComplianceSchema>;

/** The full packet. */
export const CoachInputSchema = z.object({
  userId: uuidSchema,
  generatedAt: z.string(),
  windowDays: z.number().int(),
  windowFrom: z.string(),
  profile: CoachProfileSchema,
  streak: CoachStreakSchema,
  sessions: z.array(CoachSessionSchema),
  exerciseTrend: z.array(ExerciseTrendPointSchema),
  personalRecords: z.array(PersonalRecordSchema),
  activePlan: ActivePlanSchema.nullable(),
  compliance: ComplianceSchema,
  injuryFlags: z.array(z.string()),
});
export type CoachInput = z.infer<typeof CoachInputSchema>;

/** Parse a raw packet (e.g. straight from PostgREST) into a typed `CoachInput`. */
export function parseCoachInput(raw: unknown): CoachInput {
  return CoachInputSchema.parse(raw);
}
